'use client'

import React, { useState, useEffect } from 'react'
import styles from './Feedback.module.scss'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import listCountries from '@/modules/langService/listCountries'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import CheckIcon from '@mui/icons-material/Check'
import Divider from '@mui/material/Divider'
import { PATHWAY } from '@/modules/httpService'

function FeedbackForm() {
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    location: '',
    publicationName: '',
    comments: '',
    newsletter: true
  })
  const [countryList, setCountryList] = useState<string[]>([])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    const isCheckbox =
      (e as React.ChangeEvent<HTMLInputElement>).target.type === 'checkbox'
    const checked = (e as React.ChangeEvent<HTMLInputElement>).target.checked
    setFormData({
      ...formData,
      [name]: isCheckbox ? checked : value
    })
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Handle form submission logic here
    setIsSubmitting(true)
    await PATHWAY.post('/feedbacks', formData)
    console.log(formData)
    setSubmitted(true)
    setIsSubmitting(false)
  }

  useEffect(() => {
    setCountryList(listCountries())
  }, [])

  return (
    <>
      {submitted &&
        <div className={styles.submittedWrapper}>
          <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
            Thank you, your feedback was received
          </Alert>
        </div>
      }
      {isSubmitting &&
        <div className={styles.isSubmitting}>
          <CircularProgress />
        </div>
      }
      {!submitted && !isSubmitting &&
        <form onSubmit={handleSubmit} className={styles.form}>
          <h1>Give us feedback</h1>
          <div className={styles.split}>
            <div className={styles.splitCell}>
              <TextField
                label={'First Name'}
                type="text"
                autoFocus
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                fullWidth
                className={styles.input}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  minLength: 2, // Minimum length of 2 characters
                  maxLength: 50 // Maximum length of 50 characters
                }}
              />
            </div>
            <div className={styles.splitCell}>
              <TextField
                label={'Last Name'}
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                fullWidth
                className={styles.input}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  minLength: 2, // Minimum length of 2 characters
                  maxLength: 50 // Maximum length of 50 characters
                }}
              />
            </div>
          </div>

          <div className={styles.split}>
            <div className={styles.splitCell}>
              <TextField
                label={'Email'}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                className={styles.input}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div className={styles.splitCell}>
              <FormControl fullWidth className={styles.input}>
                <InputLabel id="demo-simple-select-label">Where are you located?*</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  name='location'
                  value={formData.location}
                  label="Where are you located?"
                  required
                  onChange={handleChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // Adjust this value to control the height
                        width: 250 // Optional: Adjust width if needed
                      }
                    }
                  }}
                >
                  <MenuItem value={'United States'}>{'United States'}</MenuItem>
                  <Divider />
                  {countryList.map((location, i) => {
                    return (
                      <MenuItem key={i} value={location}>{location}</MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className={styles.div}>
            <TextField
              label={'Name of Stepping Stones article or other publication'}
              type="text"
              name="publicationName"
              value={formData.publicationName}
              onChange={handleChange}
              required
              className={styles.input}
              InputLabelProps={{ shrink: true }}
              fullWidth
              inputProps={{
                minLength: 5,
                maxLength: 100
              }}
            />
          </div>
          <div className={styles.div}>
            <TextField
              multiline
              fullWidth
              label={'Comments'}
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              required
              className={styles.textarea}
              maxRows={6}
              rows={4}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                minLength: 10,
                maxLength: 1000
              }}
            />
          </div>
          <div className={styles.div}>
            <FormControlLabel
              control={
                <Checkbox
                  {...{ inputProps: { 'aria-label': 'Checkbox demo' } }}
                  checked={formData.newsletter}
                  name='newsletter'
                  onChange={handleChange}
                />
              }
              label="Yes, I would like to sign up for your newsletter"
            />
          </div>
          <Button type="submit" variant="outlined" className={styles.button}>Submit</Button>
        </form>
      }
    </>
  )
}

export default FeedbackForm
